/**
 * Digital Studio — New Services Page-Level i18n Patch
 * AI/ML Development, Blockchain & Web3, IoT Development,
 * Legacy Modernization, Staff Augmentation, AR/VR Development
 * Page UI translations: badges, titles, CTAs, SEO metadata
 */

type PartialDigitalPage = Partial<{
  badge: string;
  title: string;
  titleHighlight: string;
  description: string;
  benefits: string[];
  ctaPrimary: string;
  ctaSecondary: string;
  seoTitle: string;
  seoDescription: string;
  featuresHeadline: string;
  featuresSubheadline: string;
  processHeadline: string;
  processSubheadline: string;
  faqHeadline: string;
  faqSubheadline: string;
  ctaHeadline: string;
  ctaSubheadline: string;
}>;

type DB = Record<string, Record<string, PartialDigitalPage>>;

export function applyDigitalNewServicesPatch(db: DB) {

  // ========== ai-ml-development ==========
  db['ai-ml-development'] = {
    fr: { badge: 'IA & ML', title: 'Développement', titleHighlight: 'IA & Machine Learning', description: 'Modèles ML sur mesure, pipelines MLOps et déploiement Edge AI pour automatiser vos opérations.', seoTitle: 'Développement IA & ML | Call IT Dev', seoDescription: 'Services de développement IA/ML : modèles personnalisés, NLP, vision, MLOps. 65% sous le marché.' },
    es: { badge: 'IA & ML', title: 'Desarrollo', titleHighlight: 'IA & Machine Learning', description: 'Modelos ML a medida, pipelines MLOps y despliegue Edge AI.', seoTitle: 'Desarrollo IA & ML | Call IT Dev' },
    ar: { badge: 'الذكاء الاصطناعي', title: 'تطوير', titleHighlight: 'الذكاء الاصطناعي والتعلم الآلي', description: 'نماذج ML مخصصة وخطوط MLOps ونشر Edge AI.', seoTitle: 'تطوير الذكاء الاصطناعي | Call IT Dev' },
    de: { badge: 'KI & ML', title: 'Entwicklung', titleHighlight: 'KI & Machine Learning', description: 'Maßgeschneiderte ML-Modelle, MLOps-Pipelines und Edge-KI.', seoTitle: 'KI & ML Entwicklung | Call IT Dev' },
    it: { badge: 'AI & ML', title: 'Sviluppo', titleHighlight: 'AI & Machine Learning', description: 'Modelli ML personalizzati, pipeline MLOps e Edge AI.', seoTitle: 'Sviluppo AI & ML | Call IT Dev' },
    pt: { badge: 'IA & ML', title: 'Desenvolvimento', titleHighlight: 'IA & Machine Learning', description: 'Modelos ML customizados, pipelines MLOps e Edge AI.', seoTitle: 'Desenvolvimento IA & ML | Call IT Dev' },
    nl: { badge: 'AI & ML', seoTitle: 'AI & ML Ontwikkeling | Call IT Dev' },
    pl: { badge: 'AI & ML', seoTitle: 'Rozwój AI & ML | Call IT Dev' },
    ru: { badge: 'ИИ & ML', title: 'Разработка', titleHighlight: 'ИИ & Machine Learning', seoTitle: 'Разработка ИИ & ML | Call IT Dev' },
    zh: { badge: 'AI & ML', seoTitle: 'AI与ML开发 | Call IT Dev' },
    ja: { badge: 'AI & ML', seoTitle: 'AI & ML開発 | Call IT Dev' },
    ko: { badge: 'AI & ML', seoTitle: 'AI & ML 개발 | Call IT Dev' },
    tr: { badge: 'AI & ML', seoTitle: 'AI & ML Geliştirme | Call IT Dev' },
    sv: { badge: 'AI & ML', seoTitle: 'AI & ML Utveckling | Call IT Dev' },
    da: { badge: 'AI & ML', seoTitle: 'AI & ML Udvikling | Call IT Dev' },
  };

  // ========== blockchain-web3 ==========
  db['blockchain-web3'] = {
    fr: { badge: 'Blockchain', title: 'Développement', titleHighlight: 'Blockchain & Web3', description: 'Smart contracts, protocoles DeFi, plateformes NFT et solutions enterprise blockchain.', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    es: { badge: 'Blockchain', title: 'Desarrollo', titleHighlight: 'Blockchain & Web3', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    ar: { badge: 'بلوكتشين', title: 'تطوير', titleHighlight: 'البلوكتشين والويب3', seoTitle: 'بلوكتشين والويب3 | Call IT Dev' },
    de: { badge: 'Blockchain', title: 'Entwicklung', titleHighlight: 'Blockchain & Web3', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    it: { badge: 'Blockchain', title: 'Sviluppo', titleHighlight: 'Blockchain & Web3', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    pt: { badge: 'Blockchain', title: 'Desenvolvimento', titleHighlight: 'Blockchain & Web3', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    nl: { badge: 'Blockchain', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    pl: { badge: 'Blockchain', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    ru: { badge: 'Блокчейн', title: 'Разработка', titleHighlight: 'Блокчейн & Web3', seoTitle: 'Блокчейн & Web3 | Call IT Dev' },
    zh: { badge: '区块链', seoTitle: '区块链与Web3 | Call IT Dev' },
    ja: { badge: 'ブロックチェーン', seoTitle: 'ブロックチェーン & Web3 | Call IT Dev' },
    ko: { badge: '블록체인', seoTitle: '블록체인 & Web3 | Call IT Dev' },
    tr: { badge: 'Blockchain', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    sv: { badge: 'Blockchain', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
    da: { badge: 'Blockchain', seoTitle: 'Blockchain & Web3 | Call IT Dev' },
  };

  // ========== iot-development ==========
  db['iot-development'] = {
    fr: { badge: 'IoT', title: 'Développement', titleHighlight: 'Internet des Objets', description: 'Plateformes IoT, edge computing, jumeaux numériques et maintenance prédictive.', seoTitle: 'Développement IoT | Call IT Dev' },
    es: { badge: 'IoT', title: 'Desarrollo', titleHighlight: 'Internet de las Cosas', seoTitle: 'Desarrollo IoT | Call IT Dev' },
    ar: { badge: 'إنترنت الأشياء', title: 'تطوير', titleHighlight: 'إنترنت الأشياء', seoTitle: 'تطوير إنترنت الأشياء | Call IT Dev' },
    de: { badge: 'IoT', title: 'Entwicklung', titleHighlight: 'Internet der Dinge', seoTitle: 'IoT Entwicklung | Call IT Dev' },
    it: { badge: 'IoT', title: 'Sviluppo', titleHighlight: 'Internet delle Cose', seoTitle: 'Sviluppo IoT | Call IT Dev' },
    pt: { badge: 'IoT', title: 'Desenvolvimento', titleHighlight: 'Internet das Coisas', seoTitle: 'Desenvolvimento IoT | Call IT Dev' },
    nl: { badge: 'IoT', seoTitle: 'IoT Ontwikkeling | Call IT Dev' },
    pl: { badge: 'IoT', seoTitle: 'Rozwój IoT | Call IT Dev' },
    ru: { badge: 'IoT', title: 'Разработка', titleHighlight: 'Интернет вещей', seoTitle: 'Разработка IoT | Call IT Dev' },
    zh: { badge: 'IoT', seoTitle: 'IoT开发 | Call IT Dev' },
    ja: { badge: 'IoT', seoTitle: 'IoT開発 | Call IT Dev' },
    ko: { badge: 'IoT', seoTitle: 'IoT 개발 | Call IT Dev' },
    tr: { badge: 'IoT', seoTitle: 'IoT Geliştirme | Call IT Dev' },
    sv: { badge: 'IoT', seoTitle: 'IoT Utveckling | Call IT Dev' },
    da: { badge: 'IoT', seoTitle: 'IoT Udvikling | Call IT Dev' },
  };

  // ========== legacy-modernization ==========
  db['legacy-modernization'] = {
    fr: { badge: 'Modernisation', title: 'Modernisation', titleHighlight: 'Systèmes Legacy', description: 'Migration incrémentale, API-first modernisation et transformation cloud-native.', seoTitle: 'Modernisation Legacy | Call IT Dev' },
    es: { badge: 'Modernización', title: 'Modernización', titleHighlight: 'Sistemas Legacy', seoTitle: 'Modernización Legacy | Call IT Dev' },
    ar: { badge: 'التحديث', title: 'تحديث', titleHighlight: 'الأنظمة القديمة', seoTitle: 'تحديث الأنظمة القديمة | Call IT Dev' },
    de: { badge: 'Modernisierung', title: 'Modernisierung', titleHighlight: 'Legacy-Systeme', seoTitle: 'Legacy Modernisierung | Call IT Dev' },
    it: { badge: 'Modernizzazione', title: 'Modernizzazione', titleHighlight: 'Sistemi Legacy', seoTitle: 'Modernizzazione Legacy | Call IT Dev' },
    pt: { badge: 'Modernização', title: 'Modernização', titleHighlight: 'Sistemas Legacy', seoTitle: 'Modernização Legacy | Call IT Dev' },
    nl: { badge: 'Modernisering', seoTitle: 'Legacy Modernisering | Call IT Dev' },
    pl: { badge: 'Modernizacja', seoTitle: 'Modernizacja Legacy | Call IT Dev' },
    ru: { badge: 'Модернизация', title: 'Модернизация', titleHighlight: 'Легаси-систем', seoTitle: 'Модернизация Legacy | Call IT Dev' },
    zh: { badge: '现代化', seoTitle: '遗留系统现代化 | Call IT Dev' },
    ja: { badge: 'モダナイゼーション', seoTitle: 'レガシー近代化 | Call IT Dev' },
    ko: { badge: '현대화', seoTitle: '레거시 현대화 | Call IT Dev' },
    tr: { badge: 'Modernizasyon', seoTitle: 'Legacy Modernizasyon | Call IT Dev' },
    sv: { badge: 'Modernisering', seoTitle: 'Legacy Modernisering | Call IT Dev' },
    da: { badge: 'Modernisering', seoTitle: 'Legacy Modernisering | Call IT Dev' },
  };

  // ========== staff-augmentation ==========
  db['staff-augmentation'] = {
    fr: { badge: 'Staff Augmentation', title: 'Renforcez', titleHighlight: 'Votre Équipe Tech', description: 'Ingénieurs seniors pré-qualifiés, matching en 48h, 65% sous le marché.', seoTitle: 'Staff Augmentation | Call IT Dev' },
    es: { badge: 'Staff Augmentation', title: 'Refuerce', titleHighlight: 'Su Equipo Tech', seoTitle: 'Staff Augmentation | Call IT Dev' },
    ar: { badge: 'تعزيز الكوادر', title: 'عزز', titleHighlight: 'فريقك التقني', seoTitle: 'تعزيز الكوادر | Call IT Dev' },
    de: { badge: 'Staff Augmentation', title: 'Verstärken Sie', titleHighlight: 'Ihr Tech-Team', seoTitle: 'Staff Augmentation | Call IT Dev' },
    it: { badge: 'Staff Augmentation', title: 'Rafforza', titleHighlight: 'Il Tuo Team Tech', seoTitle: 'Staff Augmentation | Call IT Dev' },
    pt: { badge: 'Staff Augmentation', title: 'Reforce', titleHighlight: 'Sua Equipe Tech', seoTitle: 'Staff Augmentation | Call IT Dev' },
    nl: { badge: 'Staff Augmentation', seoTitle: 'Staff Augmentation | Call IT Dev' },
    pl: { badge: 'Staff Augmentation', seoTitle: 'Staff Augmentation | Call IT Dev' },
    ru: { badge: 'Staff Augmentation', title: 'Усильте', titleHighlight: 'Вашу Команду', seoTitle: 'Staff Augmentation | Call IT Dev' },
    zh: { badge: '人员增强', seoTitle: '人员增强 | Call IT Dev' },
    ja: { badge: 'スタッフ増強', seoTitle: 'スタッフ増強 | Call IT Dev' },
    ko: { badge: '인력 보강', seoTitle: '인력 보강 | Call IT Dev' },
    tr: { badge: 'Staff Augmentation', seoTitle: 'Staff Augmentation | Call IT Dev' },
    sv: { badge: 'Staff Augmentation', seoTitle: 'Staff Augmentation | Call IT Dev' },
    da: { badge: 'Staff Augmentation', seoTitle: 'Staff Augmentation | Call IT Dev' },
  };

  // ========== ar-vr-development ==========
  db['ar-vr-development'] = {
    fr: { badge: 'AR/VR', title: 'Développement', titleHighlight: 'Réalité Augmentée & Virtuelle', description: 'Formation VR immersive, AR terrain, showrooms virtuels et spatial computing.', seoTitle: 'Développement AR/VR | Call IT Dev' },
    es: { badge: 'AR/VR', title: 'Desarrollo', titleHighlight: 'Realidad Aumentada & Virtual', seoTitle: 'Desarrollo AR/VR | Call IT Dev' },
    ar: { badge: 'AR/VR', title: 'تطوير', titleHighlight: 'الواقع المعزز والافتراضي', seoTitle: 'تطوير AR/VR | Call IT Dev' },
    de: { badge: 'AR/VR', title: 'Entwicklung', titleHighlight: 'Augmented & Virtual Reality', seoTitle: 'AR/VR Entwicklung | Call IT Dev' },
    it: { badge: 'AR/VR', title: 'Sviluppo', titleHighlight: 'Realtà Aumentata & Virtuale', seoTitle: 'Sviluppo AR/VR | Call IT Dev' },
    pt: { badge: 'AR/VR', title: 'Desenvolvimento', titleHighlight: 'Realidade Aumentada & Virtual', seoTitle: 'Desenvolvimento AR/VR | Call IT Dev' },
    nl: { badge: 'AR/VR', seoTitle: 'AR/VR Ontwikkeling | Call IT Dev' },
    pl: { badge: 'AR/VR', seoTitle: 'Rozwój AR/VR | Call IT Dev' },
    ru: { badge: 'AR/VR', title: 'Разработка', titleHighlight: 'AR & VR', seoTitle: 'Разработка AR/VR | Call IT Dev' },
    zh: { badge: 'AR/VR', seoTitle: 'AR/VR开发 | Call IT Dev' },
    ja: { badge: 'AR/VR', seoTitle: 'AR/VR開発 | Call IT Dev' },
    ko: { badge: 'AR/VR', seoTitle: 'AR/VR 개발 | Call IT Dev' },
    tr: { badge: 'AR/VR', seoTitle: 'AR/VR Geliştirme | Call IT Dev' },
    sv: { badge: 'AR/VR', seoTitle: 'AR/VR Utveckling | Call IT Dev' },
    da: { badge: 'AR/VR', seoTitle: 'AR/VR Udvikling | Call IT Dev' },
  };
}
